import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { formatCST, pickerToUTCISO } from '@/lib/time'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminMobileList } from '@/components/AdminMobileList'
import { useIsMobile } from '@/lib/hooks'

// ?target_type=anime&target_id=N 让其他页面（如 anime EditDrawer）能深链到"按目标过滤的审计"
const searchSchema = z.object({
  target_type: z.string().optional().catch(undefined),
  target_id: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/_authed/audit')({
  component: AuditPage,
  validateSearch: searchSchema,
})

interface AuditRow {
  id: number
  actor_id: string
  actor_username: string | null
  // 后端按 display_name → username → email 前缀 → '—' 兜底好的展示名
  actor_display: string
  actor_role: 'admin' | 'moderator' | 'system'
  action: string
  target_type: string
  target_id: string
  reason: string
  metadata: Record<string, unknown>
  via: string
  created_at: string
}

interface ListResult {
  ok: boolean
  total?: number
  rows?: Array<AuditRow>
  error?: string
}

const PAGE_SIZE = 50

/* ── i18n 映射 ─────────────────────────────────────────────────
 * 数据来源：扫了所有写 moderation.logs 的 RPC 字面量
 *  - action / target_type 是固定枚举（程序写死，加新动作得同步更新这里）
 *  - via 是 check 约束枚举
 *  - reason 有两类：少量 RPC 写死的 slug（admin_create / admin_delete 等）+
 *    管理员手填的自由文本（grant_moderator 系列）。translate 找不到就回显原文
 */
const ACTION_LABELS: Record<string, string> = {
  create_anime: '创建番剧',
  update_anime: '更新番剧',
  upsert_episodes: '写入剧集',
  delete_episode: '删除剧集',
  delete_play_url: '删除播放源',
  delete_anime_play_source: '摘除番剧线路',
  grant_moderator: '授予版主',
  revoke_moderator: '撤销版主',
  grant_core_user: '授予核心用户',
  revoke_core_user: '撤销核心用户',
  // 停播标记
  set_anime_hiatus: '标记停播',
  clear_anime_hiatus: '提前复播',
  admin_update_username: '改用户名',
  admin_update_avatar: '改头像',
  admin_set_membership: '设置会员',
  admin_adjust_points: '调整积分',
  admin_reset_quiz_eligibility: '重置答题资格',
  admin_change_user_password: '修改用户密码',
  // 处罚（封禁 / 禁考期）
  admin_ban_user: '禁言（封禁）',
  admin_lift_ban: '提前解封',
  admin_suspend_quiz_access: '撤销答题资格并禁考',
  admin_lift_quiz_lockout: '允许提前重考',
  // 举报受理
  dismiss_reports: '驳回举报（整批）',
  dismiss_one_report: '驳回举报（单条）',
  hide_reported_content: '隐藏被举报内容',
  shadow_reported_content: '影子被举报内容',
  delete_reported_content: '删除被举报内容',
  // 评论主动审核（comments.tsx 实施前预埋）
  hide_comment: '隐藏评论',
  shadow_comment: '影子评论',
  restore_comment: '恢复评论',
  delete_comment: '删除评论',
  // 公告
  create_announcement: '创建公告',
  update_announcement: '更新公告',
  publish_announcement: '发布公告',
  unpublish_announcement: '撤回公告',
  delete_announcement: '删除公告',
  create_friend_link: '创建友链',
  update_friend_link: '更新友链',
  activate_friend_link: '上线友链',
  deactivate_friend_link: '下线友链',
  delete_friend_link: '删除友链',
  // 转码控制面
  enqueue_transcode: '转码入队',
  evict_transcode: '转码移除',
  pin_transcode: '转码 Pin',
  unpin_transcode: '取消转码 Pin',
  upsert_rss_watch_rule: '保存 RSS 规则',
  set_rss_watch_rule_enabled: '切换 RSS 规则',
  // 试题投稿审核
  review_exam_question_submission: '审核试题投稿',
  publish_exam_question_submission: '发布试题入库',
  create_badge_definition: '创建徽章定义',
  update_badge_definition: '更新徽章定义',
  activate_badge_definition: '启用徽章定义',
  deactivate_badge_definition: '禁用徽章定义',
  grant_user_badge: '授予用户徽章',
  renew_user_badge: '续期用户徽章',
  revoke_user_badge: '撤销用户徽章',
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  anime: '番剧',
  episode: '剧集',
  user: '用户',
  comment: '评论',
  danmaku: '弹幕',
  announcement: '公告',
  friend_link: '友链',
  episode_transcode: '转码任务',
  rss_watch_rule: 'RSS 规则',
  exam_question_submission: '试题投稿',
  badge_definition: '徽章定义',
  user_badge_grant: '用户徽章授予',
}

const VIA_LABELS: Record<string, string> = {
  admin_panel: '管理面板',
  admin_dashboard: '管理后台',
  community_ui: '社区前端',
  auto: '自动',
  api: 'API',
  cron: '定时任务',
}

/** RPC 内部硬编码的 reason slug；其余 reason 多为管理员手填自由文本，原样回显 */
const REASON_LABELS: Record<string, string> = {
  admin_create: '管理员新建',
  admin_upload_episodes: '管理员上传剧集',
  admin_delete: '管理员删除',
  admin_set_anime_hiatus: '管理员标记停播',
  admin_clear_anime_hiatus: '管理员提前复播',
  admin_metadata_edit: '编辑元数据',
  admin_dismiss: '管理员驳回',
  admin_dismiss_one: '管理员驳回（单条）',
  admin_hide: '管理员隐藏',
  admin_shadow: '管理员影子',
  'rss rule changed': 'RSS 规则变更',
  'enabled rss rule': '启用 RSS 规则',
  'disabled rss rule': '停用 RSS 规则',
  // 试题投稿审核：review 未填备注时的兜底 reason / migration 发布
  accepted: '采纳（无备注）',
  migration_publish: 'migration 发布入库',
}

const ACTOR_ROLE_LABELS: Record<string, string> = {
  admin: '管理员',
  moderator: '版主',
  system: '系统',
}

function translate(map: Record<string, string>, key: string): string {
  return map[key] ?? key
}

const ACTOR_ROLE_OPTIONS = Object.entries(ACTOR_ROLE_LABELS).map(
  ([value, label]) => ({ value, label: `${label} · ${value}` }),
)
const ACTION_OPTIONS = Object.entries(ACTION_LABELS).map(([value, label]) => ({
  value,
  label: `${label} · ${value}`,
}))
const TARGET_TYPE_OPTIONS = Object.entries(TARGET_TYPE_LABELS).map(
  ([value, label]) => ({ value, label: `${label} · ${value}` }),
)
const VIA_OPTIONS = Object.entries(VIA_LABELS).map(([value, label]) => ({
  value,
  label: `${label} · ${value}`,
}))

interface Filters {
  actor_id?: string
  actor_role?: string
  action?: string
  target_type?: string
  target_id?: string
  via?: string
  range?: [Dayjs, Dayjs] | null
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function actorRoleColor(role: AuditRow['actor_role']) {
  switch (role) {
    case 'admin':
      return 'magenta'
    case 'moderator':
      return 'geekblue'
    case 'system':
    default:
      return 'default'
  }
}

function AuditPage() {
  const { target_type: targetTypeParam, target_id: targetIdParam } =
    Route.useSearch()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<Filters>({
    target_type: targetTypeParam,
    target_id: targetIdParam,
  })
  const [detail, setDetail] = useState<AuditRow | null>(null)
  const isMobile = useIsMobile()

  // 文本类输入防抖（action/target_type 已改 Select，无需防抖）
  const debouncedActorId = useDebounced(filters.actor_id?.trim() || '', 300)
  const debouncedTargetId = useDebounced(filters.target_id?.trim() || '', 300)

  // 过滤变更回首页
  useEffect(() => {
    setPage(1)
  }, [
    debouncedActorId,
    debouncedTargetId,
    filters.action,
    filters.target_type,
    filters.actor_role,
    filters.via,
    filters.range?.[0]?.valueOf(),
    filters.range?.[1]?.valueOf(),
  ])

  const listQuery = useQuery({
    queryKey: [
      'moderation_logs',
      {
        page,
        actor_id: debouncedActorId,
        actor_role: filters.actor_role,
        action: filters.action,
        target_type: filters.target_type,
        target_id: debouncedTargetId,
        via: filters.via,
        from: pickerToUTCISO(filters.range?.[0]),
        to: pickerToUTCISO(filters.range?.[1]),
      },
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // admin schema 类型未生成进 database.types.ts，cast 跳过 TS

      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_logs', {
          p_actor_id: debouncedActorId || null,
          p_actor_role: filters.actor_role ?? null,
          p_action: filters.action ?? null,
          p_target_type: filters.target_type ?? null,
          p_target_id: debouncedTargetId || null,
          p_via: filters.via ?? null,
          p_from: pickerToUTCISO(filters.range?.[0]),
          p_to: pickerToUTCISO(filters.range?.[1]),
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
        })
      if (error) throw error
      const result = data as ListResult
      if (!result.ok) throw new Error(result.error ?? 'unknown')
      return { rows: result.rows ?? [], total: result.total ?? 0 }
    },
  })

  const columns: ColumnsType<AuditRow> = useMemo(
    () => [
      {
        title: '时间',
        dataIndex: 'created_at',
        width: 170,
        render: (t: string) => (
          <Typography.Text
            style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}
          >
            {formatCST(t)}
          </Typography.Text>
        ),
      },
      {
        title: '操作者',
        width: 220,
        render: (_: unknown, row) => (
          <Space size={4} direction="vertical" style={{ rowGap: 0 }}>
            <Space size={6}>
              <Tooltip title={row.actor_role} mouseEnterDelay={0.4}>
                <Tag color={actorRoleColor(row.actor_role)}>
                  {translate(ACTOR_ROLE_LABELS, row.actor_role)}
                </Tag>
              </Tooltip>
              <Typography.Text style={{ fontSize: 13 }}>
                {row.actor_display}
              </Typography.Text>
            </Space>
            <Typography.Text
              type="secondary"
              copyable={{ text: row.actor_id }}
              style={{
                fontSize: 11,
                fontFamily: 'var(--ant-font-family-code, monospace)',
              }}
            >
              {row.actor_username ? `@${row.actor_username}` : row.actor_id}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '动作',
        dataIndex: 'action',
        width: 180,
        render: (a: string) => {
          const label = translate(ACTION_LABELS, a)
          const translated = label !== a
          return (
            <Space size={2} direction="vertical" style={{ rowGap: 0 }}>
              <Typography.Text style={{ fontSize: 13 }}>
                {label}
              </Typography.Text>
              {translated ? (
                <Typography.Text
                  type="secondary"
                  style={{
                    fontFamily: 'var(--ant-font-family-code, monospace)',
                    fontSize: 11,
                  }}
                >
                  {a}
                </Typography.Text>
              ) : null}
            </Space>
          )
        },
      },
      {
        title: '目标',
        width: 220,
        render: (_: unknown, row) => (
          <Space size={4} direction="vertical" style={{ rowGap: 0 }}>
            <Tooltip title={row.target_type} mouseEnterDelay={0.4}>
              <Tag style={{ marginRight: 0 }}>
                {translate(TARGET_TYPE_LABELS, row.target_type)}
              </Tag>
            </Tooltip>
            <Typography.Text
              type="secondary"
              copyable={{ text: row.target_id }}
              style={{
                fontSize: 11,
                fontFamily: 'var(--ant-font-family-code, monospace)',
              }}
            >
              {row.target_id}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '原因',
        dataIndex: 'reason',
        render: (r: string) => {
          const label = translate(REASON_LABELS, r)
          const translated = label !== r
          return (
            <Tooltip title={r} mouseEnterDelay={0.3}>
              <Space size={2} direction="vertical" style={{ rowGap: 0 }}>
                <Typography.Text
                  ellipsis
                  style={{ maxWidth: 320, display: 'inline-block' }}
                >
                  {label}
                </Typography.Text>
                {translated ? (
                  <Typography.Text
                    type="secondary"
                    style={{
                      fontFamily: 'var(--ant-font-family-code, monospace)',
                      fontSize: 11,
                    }}
                  >
                    {r}
                  </Typography.Text>
                ) : null}
              </Space>
            </Tooltip>
          )
        },
      },
      {
        title: '来源',
        dataIndex: 'via',
        width: 130,
        render: (v: string) => (
          <Tooltip title={v} mouseEnterDelay={0.4}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {translate(VIA_LABELS, v)}
            </Typography.Text>
          </Tooltip>
        ),
      },
      {
        title: '',
        width: 60,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Button
            size="small"
            type="link"
            onClick={(e) => {
              e.stopPropagation()
              setDetail(row)
            }}
          >
            详情
          </Button>
        ),
      },
    ],
    [],
  )

  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0

  return (
    <>
      <Card
        title="审计日志"
        extra={
          <Tooltip title={isMobile ? '刷新' : undefined}>
            <Button
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => listQuery.refetch()}
              loading={listQuery.isFetching && !listQuery.isLoading}
            >
              {isMobile ? null : '刷新'}
            </Button>
          </Tooltip>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <AdminFilterBar
            activeCount={
              (filters.target_type ? 1 : 0) +
              (filters.target_id?.trim() ? 1 : 0) +
              (filters.actor_role ? 1 : 0) +
              (filters.via ? 1 : 0) +
              (filters.actor_id?.trim() ? 1 : 0) +
              (filters.range ? 1 : 0)
            }
            primary={
              <Select
                allowClear
                showSearch
                placeholder="动作"
                suffixIcon={<SearchOutlined />}
                style={{ width: 240 }}
                value={filters.action}
                onClear={() => setFilters((f) => ({ ...f, action: undefined }))}
                onChange={(v) => setFilters((f) => ({ ...f, action: v }))}
                options={ACTION_OPTIONS}
                optionFilterProp="label"
              />
            }
          >
            <Select
              allowClear
              showSearch
              placeholder="目标类型"
              style={{ width: 180 }}
              value={filters.target_type}
              onClear={() =>
                setFilters((f) => ({ ...f, target_type: undefined }))
              }
              onChange={(v) => setFilters((f) => ({ ...f, target_type: v }))}
              options={TARGET_TYPE_OPTIONS}
              optionFilterProp="label"
            />
            <Input
              allowClear
              placeholder="目标 ID"
              value={filters.target_id}
              onChange={(e) =>
                setFilters((f) => ({ ...f, target_id: e.target.value }))
              }
              style={{ width: 200 }}
            />
            <Select
              allowClear
              placeholder="操作者角色"
              style={{ width: 160 }}
              value={filters.actor_role}
              onClear={() =>
                setFilters((f) => ({ ...f, actor_role: undefined }))
              }
              onChange={(v) => setFilters((f) => ({ ...f, actor_role: v }))}
              options={ACTOR_ROLE_OPTIONS}
            />
            <Select
              allowClear
              placeholder="来源"
              style={{ width: 180 }}
              value={filters.via}
              onClear={() => setFilters((f) => ({ ...f, via: undefined }))}
              onChange={(v) => setFilters((f) => ({ ...f, via: v }))}
              options={VIA_OPTIONS}
            />
            <Input
              allowClear
              placeholder="操作者 UID（精确）"
              value={filters.actor_id}
              onChange={(e) =>
                setFilters((f) => ({ ...f, actor_id: e.target.value }))
              }
              style={{ width: 280 }}
            />
            <DatePicker.RangePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              value={filters.range ?? null}
              onChange={(r) =>
                setFilters((f) => ({
                  ...f,
                  range: r && r[0] && r[1] ? [r[0], r[1]] : null,
                }))
              }
            />
          </AdminFilterBar>
        </div>

        <div className="admin-table-wrap">
          <Table<AuditRow>
            rowKey="id"
            size="middle"
            loading={listQuery.isLoading}
            columns={columns}
            dataSource={rows}
            onRow={(row) => ({
              onClick: () => setDetail(row),
              style: { cursor: 'pointer' },
            })}
            scroll={{ x: 1100 }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              showTotal: (totalRows) => `共 ${totalRows} 条`,
              onChange: (p) => setPage(p),
            }}
          />
        </div>

        <AdminMobileList
          rows={rows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading || listQuery.isFetching}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          renderItem={(row) => (
            <article
              className="admin-mobile-record"
              role="button"
              tabIndex={0}
              onClick={() => setDetail(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setDetail(row)
              }}
            >
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__title">
                  <Typography.Text>
                    {translate(ACTION_LABELS, row.action)}
                  </Typography.Text>
                  <div className="admin-mobile-record__meta">
                    <span>{formatCST(row.created_at, 'MM-DD HH:mm')}</span>
                  </div>
                </div>
                <Tag color={actorRoleColor(row.actor_role)}>
                  {translate(ACTOR_ROLE_LABELS, row.actor_role)}
                </Tag>
              </div>
              <div className="admin-mobile-record__badges">
                <Tag>{translate(TARGET_TYPE_LABELS, row.target_type)}</Tag>
                <Tag>{translate(VIA_LABELS, row.via)}</Tag>
              </div>
              <div className="admin-mobile-record__body">
                <Typography.Paragraph ellipsis={{ rows: 2 }}>
                  {translate(REASON_LABELS, row.reason)}
                </Typography.Paragraph>
              </div>
              <div className="admin-mobile-record__meta audit-mobile-meta">
                <span>操作者 {row.actor_display}</span>
                <span>目标 {row.target_id}</span>
                <span>#{row.id}</span>
              </div>
            </article>
          )}
        />
      </Card>

      <DetailDrawer row={detail} onClose={() => setDetail(null)} />
    </>
  )
}

function DetailDrawer({
  row,
  onClose,
}: {
  row: AuditRow | null
  onClose: () => void
}) {
  const isMobile = useIsMobile()
  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width={isMobile ? '100%' : 640}
      title={row ? `审计 #${row.id}` : ''}
      destroyOnHidden
    >
      {row ? (
        <>
          <Descriptions
            column={1}
            size="small"
            bordered
            layout={isMobile ? 'vertical' : 'horizontal'}
            items={[
              {
                label: '时间',
                children: formatCST(row.created_at, 'YYYY-MM-DD HH:mm:ss.SSS'),
              },
              {
                label: '操作者',
                children: (
                  <Space direction="vertical" size={2}>
                    <Space size={6}>
                      <Tag color={actorRoleColor(row.actor_role)}>
                        {translate(ACTOR_ROLE_LABELS, row.actor_role)} ·{' '}
                        {row.actor_role}
                      </Tag>
                      <span>{row.actor_display}</span>
                    </Space>
                    {row.actor_username ? (
                      <Typography.Text type="secondary">
                        @{row.actor_username}
                      </Typography.Text>
                    ) : null}
                    <Typography.Text
                      type="secondary"
                      copyable
                      style={{
                        fontSize: 12,
                        fontFamily: 'var(--ant-font-family-code, monospace)',
                      }}
                    >
                      {row.actor_id}
                    </Typography.Text>
                  </Space>
                ),
              },
              {
                label: '动作',
                children: (
                  <Space direction="vertical" size={2}>
                    <span>{translate(ACTION_LABELS, row.action)}</span>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontFamily: 'var(--ant-font-family-code, monospace)',
                        fontSize: 12,
                      }}
                    >
                      {row.action}
                    </Typography.Text>
                  </Space>
                ),
              },
              {
                label: '目标',
                children: (
                  <Space direction="vertical" size={2}>
                    <Tag>
                      {translate(TARGET_TYPE_LABELS, row.target_type)} ·{' '}
                      {row.target_type}
                    </Tag>
                    <Typography.Text
                      copyable
                      style={{
                        fontFamily: 'var(--ant-font-family-code, monospace)',
                      }}
                    >
                      {row.target_id}
                    </Typography.Text>
                  </Space>
                ),
              },
              {
                label: '原因',
                children: (() => {
                  const label = translate(REASON_LABELS, row.reason)
                  const translated = label !== row.reason
                  return (
                    <Space direction="vertical" size={2}>
                      <span>{label}</span>
                      {translated ? (
                        <Typography.Text
                          type="secondary"
                          style={{
                            fontFamily:
                              'var(--ant-font-family-code, monospace)',
                            fontSize: 12,
                          }}
                        >
                          {row.reason}
                        </Typography.Text>
                      ) : null}
                    </Space>
                  )
                })(),
              },
              {
                label: '来源',
                children: `${translate(VIA_LABELS, row.via)} · ${row.via}`,
              },
              {
                label: 'metadata',
                children: (
                  <pre
                    style={{
                      margin: 0,
                      padding: 12,
                      background: 'var(--ant-color-fill-tertiary)',
                      borderRadius: 4,
                      fontSize: 12,
                      maxHeight: 360,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(row.metadata, null, 2)}
                  </pre>
                ),
              },
            ]}
          />
        </>
      ) : null}
    </Drawer>
  )
}
